/**
 * RejectionModal (Phase 23.2.4 — Living SOPs)
 *
 * Modal shown when a trainer clicks Reject on a pending-review proposal.
 * Captures a required rejection reason + an optional note, then POSTs to
 * /api/sops/proposals/:proposalId/reject. On success the parent
 * (ProposalDetailPage) navigates back to the inbox.
 *
 * Reason values map 1:1 to LIVING_SOPS_CONFIG.SUPPRESSION_TTL_DAYS on the
 * backend (§3.8 table):
 *  - already_correct → 30 days suppress
 *  - missing_context → 7 days suppress
 *  - cosmetic        → 14 days suppress
 *  - safety          → permanent (requires trainer manual clear via SOP
 *                      Health tab pill in Phase 23.x)
 *  - other           → 7 days suppress
 *
 * Accessibility:
 *  - role="dialog" + aria-modal="true"
 *  - Escape closes (when not submitting)
 *  - Focus-lands on the dialog root so screen readers announce the heading
 *  - The reason radio group uses a shared name so keyboard nav is native
 *  - Cancel/Reject buttons are keyboard-reachable via tab; no forced trap
 *    because there is no modal underneath the text input to contend with.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, X, XCircle } from 'lucide-react';
import { useRejectProposal } from '../../../hooks/useSopProposals';

// Must match the REJECTION_REASONS enum on the backend
// (apps/api/src/routes/sops.ts). Keep these lists in lockstep — the server
// returns 400 on unknown values.
export type RejectionReason =
  | 'already_correct'
  | 'missing_context'
  | 'safety'
  | 'cosmetic'
  | 'other';

interface ReasonOption {
  value: RejectionReason;
  title: string;
  description: string;
}

const REASON_OPTIONS: ReasonOption[] = [
  {
    value: 'already_correct',
    title: 'The SOP is already correct as-is',
    description:
      'Suppress future struggle signals for the affected steps for 30 days.',
  },
  {
    value: 'missing_context',
    title: 'The AI is missing context',
    description:
      'Plausible but thin — re-try drafting in 7 days once more signals arrive.',
  },
  {
    value: 'cosmetic',
    title: 'Cosmetic change — not worth a version bump',
    description: 'Right in spirit, wording off. Suppress for 14 days.',
  },
  {
    value: 'safety',
    title: 'Safety-critical — do not propose again',
    description:
      'Permanent block. Requires explicit trainer unblock via SOP Health to reopen.',
  },
  {
    value: 'other',
    title: 'Other',
    description: 'Short default suppression (7 days).',
  },
];

const NOTE_MAX_LENGTH = 2000;

interface RejectionModalProps {
  isOpen: boolean;
  proposalId: string;
  onClose: () => void;
  onRejected: () => void;
}

export default function RejectionModal({
  isOpen,
  proposalId,
  onClose,
  onRejected,
}: RejectionModalProps) {
  const [reason, setReason] = useState<RejectionReason | ''>('');
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const rejectMutation = useRejectProposal();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset local state each time the modal opens so stale values don't leak
  // between sequential Reject cycles (e.g. trainer rejects, navigates, comes
  // back and clicks Reject again).
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setNote('');
      setValidationError(null);
      setServerError(null);
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  // Escape closes the modal when not submitting. We intentionally do NOT
  // wire Escape to close while a reject mutation is in-flight — the POST
  // is already committed and the user should wait for the server response.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !rejectMutation.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, rejectMutation.isPending]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setValidationError(null);
      setServerError(null);

      if (!reason) {
        setValidationError('Pick a rejection reason.');
        return;
      }

      rejectMutation.mutate(
        {
          proposalId,
          reason,
          note: note.trim().length > 0 ? note.trim() : undefined,
        },
        {
          onSuccess: () => {
            onRejected();
          },
          onError: (err) => {
            const msg =
              err instanceof Error
                ? err.message
                : 'Reject failed. Please try again.';
            setServerError(msg);
          },
        }
      );
    },
    [reason, note, proposalId, rejectMutation, onRejected]
  );

  if (!isOpen) return null;

  const isSubmitting = rejectMutation.isPending;
  const remainingChars = NOTE_MAX_LENGTH - note.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rejection-modal-title"
      onClick={() => {
        // Backdrop click closes only when not submitting.
        if (!isSubmitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <XCircle size={20} className="text-rose-600" strokeWidth={2.5} />
            <h2
              id="rejection-modal-title"
              className="text-lg font-bold text-gray-900"
            >
              Reject this proposal
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <p className="mt-2 text-sm text-gray-600">
          The reason you pick sets a suppression window — the AI drafter will
          skip affected steps for that period so the same proposal doesn't
          come back immediately.
        </p>

        <form onSubmit={handleSubmit} className="mt-5">
          <fieldset
            aria-labelledby="rejection-reason-legend"
            aria-required="true"
            disabled={isSubmitting}
          >
            <legend
              id="rejection-reason-legend"
              className="mb-2 text-sm font-semibold text-gray-900"
            >
              Reason <span className="text-rose-600">*</span>
            </legend>
            <div className="space-y-2">
              {REASON_OPTIONS.map((opt) => {
                const checked = reason === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      checked
                        ? 'border-guild-green bg-guild-mint/20'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rejection-reason"
                      value={opt.value}
                      checked={checked}
                      onChange={() => {
                        setReason(opt.value);
                        setValidationError(null);
                      }}
                      className="mt-1 h-4 w-4 border-gray-300 bg-white text-guild-green focus:ring-guild-green/30"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {opt.title}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-600">
                        {opt.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
            {validationError && (
              <p
                role="alert"
                className="mt-2 flex items-center gap-1.5 text-xs text-rose-700"
              >
                <AlertCircle size={12} strokeWidth={2.5} />
                {validationError}
              </p>
            )}
          </fieldset>

          <div className="mt-5">
            <label
              htmlFor="rejection-note"
              className="mb-1 block text-sm font-semibold text-gray-900"
            >
              Note (optional)
            </label>
            <textarea
              id="rejection-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
              disabled={isSubmitting}
              maxLength={NOTE_MAX_LENGTH}
              rows={3}
              placeholder="Add context for the proposer and future reviewers."
              className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/30 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 flex items-center justify-between text-xs text-gray-500">
              <span>
                Manual proposals: the authoring trainer is notified when you
                reject.
              </span>
              <span
                className={
                  remainingChars < 200
                    ? 'font-medium text-amber-700'
                    : 'text-gray-500'
                }
              >
                {remainingChars} left
              </span>
            </p>
          </div>

          {serverError && (
            <p
              role="alert"
              className="mt-4 flex items-start gap-1.5 rounded-md bg-rose-50 p-2 text-xs text-rose-800"
            >
              <AlertCircle
                size={12}
                className="mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
              <span>{serverError}</span>
            </p>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reason}
              className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    size={14}
                    strokeWidth={2.5}
                    className="animate-spin"
                  />
                  Rejecting…
                </>
              ) : (
                <>
                  <XCircle size={14} strokeWidth={2.5} />
                  Reject
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
